import { c as _c } from "react/compiler-runtime";
import { COUNTRIES, identityDisForShortTextFormat, isVaultTemplateRef } from '@onefootprint/core';
import type { CustomRequirementShortTextFormat } from '@onefootprint/request-types';
import { Form, type TemplateInputProps } from '@onefootprint/ui';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAutoDataIdentifier } from '../../../hooks/use-auto-data-identifier';
import type { CustomQuestionsFormData } from '../../../types';
import orderedPair from '../../../utils/ordered-pair';
import DataIdentifierField from '../data-identifier-field';
type ShortTextFormProps = {
  questionIndex: number;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
};
const ShortTextForm = t0 => {
  const $ = _c(162);
  const {
    questionIndex,
    autocompleteItems
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const {
    control,
    setValue,
    getValues,
    trigger
  } = useFormContext();
  const t1 = `questions.${questionIndex}.format`;
  const t2 = `questions.${questionIndex}.minLength`;
  const t3 = `questions.${questionIndex}.maxLength`;
  const t4 = `questions.${questionIndex}.minAge`;
  const t5 = `questions.${questionIndex}.maxAge`;
  let t6;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = [t1, t2, t3, t4, t5];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = t6 as never;
  let t8;
  if ($[6] !== control || $[7] !== t7) {
    t8 = {
      control,
      name: t7
    };
    $[6] = control;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const [format, minLength, maxLength, minAge, maxAge] = useWatch(t8) as [string, unknown, unknown, unknown, unknown];
  let t9;
  if ($[9] !== questionIndex || $[10] !== trigger) {
    t9 = () => {
      trigger(`questions.${questionIndex}.minLength` as never);
      trigger(`questions.${questionIndex}.maxLength` as never);
    };
    $[9] = questionIndex;
    $[10] = trigger;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== maxLength || $[13] !== minLength || $[14] !== questionIndex || $[15] !== trigger) {
    t10 = [minLength, maxLength, questionIndex, trigger];
    $[12] = maxLength;
    $[13] = minLength;
    $[14] = questionIndex;
    $[15] = trigger;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  if ($[17] !== questionIndex || $[18] !== trigger) {
    t11 = () => {
      trigger(`questions.${questionIndex}.minAge` as never);
      trigger(`questions.${questionIndex}.maxAge` as never);
    };
    $[17] = questionIndex;
    $[18] = trigger;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== maxAge || $[21] !== minAge || $[22] !== questionIndex || $[23] !== trigger) {
    t12 = [minAge, maxAge, questionIndex, trigger];
    $[20] = maxAge;
    $[21] = minAge;
    $[22] = questionIndex;
    $[23] = trigger;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  useEffect(t11, t12);
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = ["any", "email", "vehicle_identification_number", "zip"];
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const placeholderSupportedFormats = t13;
  useAutoDataIdentifier(questionIndex);
  let t14;
  if ($[26] !== getValues || $[27] !== questionIndex || $[28] !== setValue) {
    t14 = next => {
      if (next !== "any") {
        setValue(`questions.${questionIndex}.regex`, undefined);
        setValue(`questions.${questionIndex}.minLength`, undefined);
        setValue(`questions.${questionIndex}.maxLength`, undefined);
        setValue(`questions.${questionIndex}.errorMessage`, undefined);
      }
      if (next !== "dob") {
        setValue(`questions.${questionIndex}.minAge`, undefined);
        setValue(`questions.${questionIndex}.maxAge`, undefined);
      }
      if (next !== "email") {
        setValue(`questions.${questionIndex}.disablePlusAddressing`, undefined);
      } else {
        setValue(`questions.${questionIndex}.disablePlusAddressing`, false);
      }
      if (next !== "country") {
        setValue(`questions.${questionIndex}.supportedCountries`, undefined);
      }
      if (!placeholderSupportedFormats.includes(next)) {
        setValue(`questions.${questionIndex}.placeholder`, undefined);
      }
      const currentDi = getValues(`questions.${questionIndex}.dataIdentifier`) || "";
      if (!currentDi.startsWith("id.")) {
        return;
      }
      const allowed = identityDisForShortTextFormat(next);
      if (allowed.includes(currentDi)) {
        return;
      }
      const nextDi = allowed.length === 1 ? allowed[0] : `custom.${currentDi.replace(/^id\./, "")}`;
      setValue(`questions.${questionIndex}.dataIdentifier`, nextDi);
    };
    $[26] = getValues;
    $[27] = questionIndex;
    $[28] = setValue;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const clearFormatOptions = t14;
  const t15 = `questions.${questionIndex}.label`;
  let t16;
  if ($[30] !== t) {
    t16 = t(_temp);
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t(_temp2);
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== autocompleteItems) {
    t18 = autocompleteItems ?? {};
    $[34] = autocompleteItems;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t15 || $[37] !== t16 || $[38] !== t17 || $[39] !== t18) {
    t19 = <Form.TemplateInput name={t15} label={t16} placeholder={t17} size="compact" autocompleteItems={t18} />;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = `questions.${questionIndex}.required`;
  let t21;
  if ($[41] !== t) {
    t21 = t(_temp3);
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t20 || $[44] !== t21) {
    t22 = <Form.Toggle name={t20} label={t21} size="compact" />;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t19 || $[47] !== t22) {
    t23 = <div className="flex flex-col gap-6">{t19}{t22}</div>;
    $[46] = t19;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const t24 = `questions.${questionIndex}.format`;
  let t25;
  if ($[49] !== t) {
    t25 = t(_temp4);
    $[49] = t;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== clearFormatOptions) {
    t26 = value => clearFormatOptions(value as CustomRequirementShortTextFormat['format']);
    $[51] = clearFormatOptions;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t) {
    t27 = t(_temp5);
    $[53] = t;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t27) {
    t28 = {
      required: t27
    };
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t) {
    t29 = t(_temp6);
    $[57] = t;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t29) {
    t30 = {
      label: t29,
      value: "any"
    };
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t) {
    t31 = t(_temp7);
    $[61] = t;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== t31) {
    t32 = {
      label: t31,
      value: "phone_number"
    };
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== t) {
    t33 = t(_temp8);
    $[65] = t;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  let t34;
  if ($[67] !== t33) {
    t34 = {
      label: t33,
      value: "email"
    };
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== t) {
    t35 = t(_temp9);
    $[69] = t;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t35) {
    t36 = {
      label: t35,
      value: "ssn9"
    };
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] !== t) {
    t37 = t(_temp10);
    $[73] = t;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t37) {
    t38 = {
      label: t37,
      value: "us_tax_id"
    };
    $[75] = t37;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== t) {
    t39 = t(_temp11);
    $[77] = t;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  let t40;
  if ($[79] !== t39) {
    t40 = {
      label: t39,
      value: "zip"
    };
    $[79] = t39;
    $[80] = t40;
  } else {
    t40 = $[80];
  }
  let t41;
  if ($[81] !== t) {
    t41 = t(_temp12);
    $[81] = t;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t41) {
    t42 = {
      label: t41,
      value: "date"
    };
    $[83] = t41;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== t) {
    t43 = t(_temp13);
    $[85] = t;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t43) {
    t44 = {
      label: t43,
      value: "vehicle_identification_number"
    };
    $[87] = t43;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== t) {
    t45 = t(_temp14);
    $[89] = t;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t45) {
    t46 = {
      label: t45,
      value: "dob"
    };
    $[91] = t45;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t) {
    t47 = t(_temp15);
    $[93] = t;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  let t48;
  if ($[95] !== t47) {
    t48 = {
      label: t47,
      value: "country"
    };
    $[95] = t47;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== t30 || $[98] !== t32 || $[99] !== t34 || $[100] !== t36 || $[101] !== t38 || $[102] !== t40 || $[103] !== t42 || $[104] !== t44 || $[105] !== t46 || $[106] !== t48) {
    t49 = [t30, t32, t34, t36, t38, t40, t42, t44, t46, t48];
    $[97] = t30;
    $[98] = t32;
    $[99] = t34;
    $[100] = t36;
    $[101] = t38;
    $[102] = t40;
    $[103] = t42;
    $[104] = t44;
    $[105] = t46;
    $[106] = t48;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] !== t24 || $[109] !== t25 || $[110] !== t26 || $[111] !== t28 || $[112] !== t49) {
    t50 = <Form.Select name={t24} label={t25} size="compact" onChange={t26} rules={t28} options={t49} />;
    $[108] = t24;
    $[109] = t25;
    $[110] = t26;
    $[111] = t28;
    $[112] = t49;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] !== format || $[115] !== questionIndex || $[116] !== t) {
    t51 = format === "email" && <Form.Toggle name={`questions.${questionIndex}.disablePlusAddressing`} label={t(_temp16)} hint={t(_temp17)} size="compact" />;
    $[114] = format;
    $[115] = questionIndex;
    $[116] = t;
    $[117] = t51;
  } else {
    t51 = $[117];
  }
  let t52;
  if ($[118] !== format || $[119] !== questionIndex || $[120] !== t) {
    t52 = placeholderSupportedFormats.includes(format as CustomRequirementShortTextFormat['format']) && <Form.Input name={`questions.${questionIndex}.placeholder`} label={t(_temp18)} placeholder={t(_temp19)} size="compact" />;
    $[118] = format;
    $[119] = questionIndex;
    $[120] = t;
    $[121] = t52;
  } else {
    t52 = $[121];
  }
  let t53;
  if ($[122] !== autocompleteItems || $[123] !== format || $[124] !== getValues || $[125] !== questionIndex || $[126] !== t) {
    t53 = format === "any" && <><Form.TemplateInput name={`questions.${questionIndex}.regex`} label={t(_temp20)} placeholder={t(_temp21)} tooltip={{
        text: t(_temp22)
      }} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
        validate: value_0 => {
          if (typeof value_0 !== "string" || value_0.length === 0) {
            return true;
          }
          if (isVaultTemplateRef(value_0)) {
            return true;
          }
          try {
            new RegExp(value_0);
            return true;
          } catch {
            return t(_temp23);
          }
        }
      }} /><Form.TemplateInput name={`questions.${questionIndex}.errorMessage`} label={t(_temp24)} placeholder={t(_temp25)} hint={t(_temp26)} size="compact" autocompleteItems={autocompleteItems ?? {}} /><div className="flex gap-4"><Form.TemplateInput name={`questions.${questionIndex}.minLength`} label={t(_temp27)} placeholder={t(_temp28)} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
          validate: value_1 => {
            if (typeof value_1 !== "string" || value_1.length === 0) {
              return true;
            }
            if (isVaultTemplateRef(value_1)) {
              return true;
            }
            const n = Number(value_1);
            if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
              return t(_temp29);
            }
            const pair = orderedPair(value_1, getValues(`questions.${questionIndex}.maxLength`));
            return !pair || pair.min <= pair.max || t(_temp30);
          }
        }} /><Form.TemplateInput name={`questions.${questionIndex}.maxLength`} label={t(_temp31)} placeholder={t(_temp32)} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
          validate: value_2 => {
            if (typeof value_2 !== "string" || value_2.length === 0) {
              return true;
            }
            if (isVaultTemplateRef(value_2)) {
              return true;
            }
            const n_0 = Number(value_2);
            if (!Number.isFinite(n_0) || !Number.isInteger(n_0) || n_0 < 0) {
              return t(_temp33);
            }
            const pair_0 = orderedPair(getValues(`questions.${questionIndex}.minLength`), value_2);
            return !pair_0 || pair_0.min <= pair_0.max || t(_temp34);
          }
        }} /></div></>;
    $[122] = autocompleteItems;
    $[123] = format;
    $[124] = getValues;
    $[125] = questionIndex;
    $[126] = t;
    $[127] = t53;
  } else {
    t53 = $[127];
  }
  let t54;
  if ($[128] !== autocompleteItems || $[129] !== format || $[130] !== getValues || $[131] !== questionIndex || $[132] !== t) {
    t54 = format === "dob" && <div className="flex gap-4"><Form.TemplateInput name={`questions.${questionIndex}.minAge`} label={t(_temp35)} placeholder={t(_temp36)} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
        validate: value_3 => {
          if (typeof value_3 !== "string" || value_3.length === 0) {
            return true;
          }
          if (isVaultTemplateRef(value_3)) {
            return true;
          }
          const n_1 = Number(value_3);
          if (!Number.isFinite(n_1) || !Number.isInteger(n_1) || n_1 < 0) {
            return t(_temp37);
          }
          const pair_1 = orderedPair(value_3, getValues(`questions.${questionIndex}.maxAge`));
          return !pair_1 || pair_1.min <= pair_1.max || t(_temp38);
        }
      }} /><Form.TemplateInput name={`questions.${questionIndex}.maxAge`} label={t(_temp39)} placeholder={t(_temp40)} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
        validate: value_4 => {
          if (typeof value_4 !== "string" || value_4.length === 0) {
            return true;
          }
          if (isVaultTemplateRef(value_4)) {
            return true;
          }
          const n_2 = Number(value_4);
          if (!Number.isFinite(n_2) || !Number.isInteger(n_2) || n_2 < 0) {
            return t(_temp41);
          }
          const pair_2 = orderedPair(getValues(`questions.${questionIndex}.minAge`), value_4);
          return !pair_2 || pair_2.min <= pair_2.max || t(_temp42);
        }
      }} /></div>;
    $[128] = autocompleteItems;
    $[129] = format;
    $[130] = getValues;
    $[131] = questionIndex;
    $[132] = t;
    $[133] = t54;
  } else {
    t54 = $[133];
  }
  let t55;
  if ($[134] !== format || $[135] !== questionIndex || $[136] !== t) {
    t55 = format === "country" && <Form.MultiSelect name={`questions.${questionIndex}.supportedCountries`} label={t(_temp43)} tooltip={{
      text: t(_temp44)
    }} placeholder={t(_temp45)} size="compact" options={COUNTRIES.map(_temp46)} />;
    $[134] = format;
    $[135] = questionIndex;
    $[136] = t;
    $[137] = t55;
  } else {
    t55 = $[137];
  }
  let t56;
  if ($[138] !== t) {
    t56 = t(_temp47);
    $[138] = t;
    $[139] = t56;
  } else {
    t56 = $[139];
  }
  let t57;
  if ($[140] !== t) {
    t57 = t(_temp48);
    $[140] = t;
    $[141] = t57;
  } else {
    t57 = $[141];
  }
  let t58;
  if ($[142] !== t) {
    t58 = t(_temp49);
    $[142] = t;
    $[143] = t58;
  } else {
    t58 = $[143];
  }
  let t59;
  if ($[144] !== t58) {
    t59 = {
      text: t58
    };
    $[144] = t58;
    $[145] = t59;
  } else {
    t59 = $[145];
  }
  let t60;
  if ($[146] !== questionIndex || $[147] !== t56 || $[148] !== t57 || $[149] !== t59) {
    t60 = <DataIdentifierField questionIndex={questionIndex} label={t56} placeholder={t57} tooltip={t59} />;
    $[146] = questionIndex;
    $[147] = t56;
    $[148] = t57;
    $[149] = t59;
    $[150] = t60;
  } else {
    t60 = $[150];
  }
  let t61;
  if ($[151] !== t50 || $[152] !== t51 || $[153] !== t52 || $[154] !== t53 || $[155] !== t54 || $[156] !== t55 || $[157] !== t60) {
    t61 = <div className="flex flex-col gap-6">{t50}{t51}{t52}{t53}{t54}{t55}{t60}</div>;
    $[151] = t50;
    $[152] = t51;
    $[153] = t52;
    $[154] = t53;
    $[155] = t54;
    $[156] = t55;
    $[157] = t60;
    $[158] = t61;
  } else {
    t61 = $[158];
  }
  let t62;
  if ($[159] !== t23 || $[160] !== t61) {
    t62 = <>{t23}{t61}</>;
    $[159] = t23;
    $[160] = t61;
    $[161] = t62;
  } else {
    t62 = $[161];
  }
  return t62;
};
export default ShortTextForm;
function _temp($) {
  return $.form.label;
}
function _temp2($_0) {
  return $_0.form.labelPlaceholder;
}
function _temp3($_1) {
  return $_1.form.required;
}
function _temp4($_2) {
  return $_2.form.format;
}
function _temp5($_3) {
  return $_3.form.formatRequired;
}
function _temp6($_4) {
  return $_4.formats.any;
}
function _temp7($_5) {
  return $_5.formats.phoneNumber;
}
function _temp8($_6) {
  return $_6.formats.email;
}
function _temp9($_7) {
  return $_7.formats.ssn9;
}
function _temp10($_8) {
  return $_8.formats.usTaxId;
}
function _temp11($_9) {
  return $_9.formats.zip;
}
function _temp12($_10) {
  return $_10.formats.date;
}
function _temp13($_11) {
  return $_11.formats.vin;
}
function _temp14($_12) {
  return $_12.formats.dob;
}
function _temp15($_13) {
  return $_13.formats.country;
}
function _temp16($_14) {
  return $_14.form.disablePlusAddressing;
}
function _temp17($_15) {
  return $_15.form.disablePlusAddressingHint;
}
function _temp18($_16) {
  return $_16.form.placeholder;
}
function _temp19($_17) {
  return $_17.form.placeholderPlaceholder;
}
function _temp20($_18) {
  return $_18.form.regex;
}
function _temp21($_19) {
  return $_19.form.regexPlaceholder;
}
function _temp22($_20) {
  return $_20.form.regexTooltip;
}
function _temp23($_21) {
  return $_21.form.regexInvalid;
}
function _temp24($_22) {
  return $_22.form.errorMessage;
}
function _temp25($_23) {
  return $_23.form.errorMessagePlaceholder;
}
function _temp26($_24) {
  return $_24.form.errorMessageHint;
}
function _temp27($_25) {
  return $_25.form.minLength;
}
function _temp28($_26) {
  return $_26.form.minLengthPlaceholder;
}
function _temp29($_27) {
  return $_27.form.lengthNonNegative;
}
function _temp30($_28) {
  return $_28.form.lengthMinAboveMax;
}
function _temp31($_29) {
  return $_29.form.maxLength;
}
function _temp32($_30) {
  return $_30.form.maxLengthPlaceholder;
}
function _temp33($_31) {
  return $_31.form.lengthNonNegative;
}
function _temp34($_32) {
  return $_32.form.lengthMaxBelowMin;
}
function _temp35($_33) {
  return $_33.form.minAge;
}
function _temp36($_34) {
  return $_34.form.minAgePlaceholder;
}
function _temp37($_35) {
  return $_35.form.ageNonNegative;
}
function _temp38($_36) {
  return $_36.form.ageMinAboveMax;
}
function _temp39($_37) {
  return $_37.form.maxAge;
}
function _temp40($_38) {
  return $_38.form.maxAgePlaceholder;
}
function _temp41($_39) {
  return $_39.form.ageNonNegative;
}
function _temp42($_40) {
  return $_40.form.ageMaxBelowMin;
}
function _temp43($_41) {
  return $_41.form.supportedCountriesLabel;
}
function _temp44($_42) {
  return $_42.form.supportedCountriesTooltip;
}
function _temp45($_43) {
  return $_43.form.supportedCountriesPlaceholder;
}
function _temp46(c) {
  return {
    value: c.value,
    label: c.label
  };
}
function _temp47($_44) {
  return $_44.form.dataIdentifier;
}
function _temp48($_45) {
  return $_45.form.dataIdentifierPlaceholder;
}
function _temp49($_46) {
  return $_46.form.dataIdentifierTooltip;
}